import { useRef, useState } from 'react'

import useTrackClick from '~/events/trackers/useTrackClick'
import useTrackEvents from '~/events/trackers/useTrackEvents'
import useTrackScreenView from '~/events/trackers/useTrackScreenView'
import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import LumosityCircleColor from '~/images/icons/Brand/LumosityCircleColor.svg'
import ChevronLeftIcon from '~/images/icons/SystemOutlined/ChevronLeft.svg'
import CloseIcon from '~/images/icons/SystemOutlined/Close.svg'
import { Button } from '~/ui/Button/Button'
import Modal from '~/ui/Modal/Modal'

type FeedbackFlowScreenType = 'FeedbackDecider' | 'GoodFeedback' | 'BadFeedback'

export const FeedbackFlowScreens = ({ onClose, isFlagshipUser }: { onClose: () => void; isFlagshipUser: boolean }) => {
  const [screen, setScreen] = useState<FeedbackFlowScreenType>('FeedbackDecider')

  return (
    <>
      {screen === 'FeedbackDecider' && (
        <FeedbackDecider
          onClose={onClose}
          onGoodFeedback={() => setScreen('GoodFeedback')}
          onBadFeedback={() => setScreen('BadFeedback')}
          isFlagshipUser={isFlagshipUser}
        />
      )}
      {screen === 'GoodFeedback' && <GoodFeedback onBack={() => setScreen('FeedbackDecider')} onClose={onClose} />}
      {screen === 'BadFeedback' && (
        <BadFeedback onBack={() => setScreen('FeedbackDecider')} onClose={onClose} isFlagshipUser={isFlagshipUser} />
      )}
    </>
  )
}

const FeedbackDecider = ({
  onClose,
  onGoodFeedback,
  onBadFeedback,
  isFlagshipUser,
}: {
  onClose: () => void
  onGoodFeedback: () => void
  onBadFeedback: () => void
  isFlagshipUser: boolean
}) => {
  useTrackScreenView({ screen_name: 'provide_feedback' })
  const { trackCta } = useTrackClick()

  const t = useCommonTx('feedback')

  const texts = {
    title: isFlagshipUser ? t('deciderFlagship.title') : t('decider.title'),
    p1: isFlagshipUser ? t('deciderFlagship.p1') : t('decider.p1'),
    yes: t('decider.yes'),
    no: t('decider.no'),
  }

  const _onGoodFeedback = () => {
    trackCta({
      type: 'button',
      text: texts.yes,
      screen_name: 'provide_feedback',
      click_name: 'provide_feedback_yes_love_it',
      destination: 'positive_feedback',
    })
    onGoodFeedback()
  }

  const _onBadFeedback = () => {
    trackCta({
      type: 'button',
      text: texts.no,
      screen_name: 'provide_feedback',
      click_name: 'provide_feedback_could_be_better',
      destination: 'negative_feedback',
    })
    onBadFeedback()
  }

  const _onClose = () => {
    trackCta({
      type: 'button',
      text: 'X',
      screen_name: 'provide_feedback',
      click_name: 'provide_feedback_dismiss',
      destination: 'home',
    })
    onClose()
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      className='relative w-full max-w-[592px] flex flex-col items-center p-6 sm:p-[64px] rounded-lg'
    >
      <Button kind='icon' size='none' className='absolute top-4 right-4' onClick={_onClose}>
        <CloseIcon title='Close' className='w-8 h-8' />
      </Button>

      <div className='flex flex-col items-center justify-center text-center mx-auto'>
        <LumosityCircleColor title='logo' className='w-[80px] h-[80px] mb-3' />
        <h2 className='text-heading-3 mb-8'>{texts.title}</h2>
        <p className='text-body-text-2 text-text-subdued mb-8'>{texts.p1}</p>
        <div className='w-full flex flex-col items-center justify-center max-w-[300px] gap-3'>
          <Button kind='primary' className='w-full' onClick={_onGoodFeedback}>
            {texts.yes}
          </Button>
          <Button kind='secondary' className='w-full' onClick={_onBadFeedback}>
            {texts.no}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const GoodFeedback = ({ onBack, onClose }: { onBack: () => void; onClose: () => void }) => {
  useTrackScreenView({ screen_name: 'positive_feedback' })
  const { trackCta } = useTrackClick()
  const { trackEvent } = useTrackEvents()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const t = useCommonTx('feedback')
  const ct = useCommonTx('common')

  const texts = {
    title: t('positive.title'),
    p1: t('positive.p1'),
    inputPlaceholder: ct('commentsOrQuestions'),
    yes: ct('submitFeedback'),
    no: ct('cancel'),
  }

  const _onBack = () => {
    trackCta({
      type: 'button',
      text: '<',
      screen_name: 'positive_feedback',
      click_name: 'positive_feedback_back',
      destination: 'provide_feedback',
    })
    onBack()
  }

  const _onSubmit = () => {
    trackCta({
      type: 'button',
      text: texts.yes,
      screen_name: 'positive_feedback',
      click_name: 'positive_feedback_submit',
      destination: 'home',
    })
    trackEvent('feedback_submit', {
      feedback: textAreaRef.current?.value || '',
      screen_name: 'positive_feedback',
    })
    onClose()
  }

  const _onCancel = () => {
    trackCta({
      type: 'button',
      text: texts.no,
      screen_name: 'positive_feedback',
      click_name: 'positive_feedback_cancel',
      destination: 'home',
    })

    onClose()
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      className='relative w-full max-w-[592px] flex flex-col items-center p-6 sm:p-[64px] rounded-lg'
    >
      <Button kind='icon' size='none' className='absolute top-4 left-4' onClick={_onBack}>
        <ChevronLeftIcon title='Close' className='w-8 h-8' />
      </Button>

      <div className='w-full flex flex-col items-center justify-center text-center mx-auto'>
        <img src='/assets/images/feedback/bear.png' alt='bear' className='w-[117px] h-[100px] mb-8' />
        <h2 className='text-heading-3 mb-4'>{texts.title}</h2>
        <p className='text-body-text-2 text-text-subdued mb-8'>{texts.p1}</p>
        <textarea
          ref={textAreaRef}
          placeholder={texts.inputPlaceholder}
          className='w-full h-[200px] border border-border-subdued rounded-md py-3 px-4 mb-8 text-body-text-2'
        />
        <div className='w-full flex flex-col items-center justify-center text-center max-w-[300px] gap-3'>
          <Button kind='primary' className='w-full' onClick={_onSubmit}>
            {texts.yes}
          </Button>
          <Button kind='secondary' className='w-full' onClick={_onCancel}>
            {texts.no}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const BadFeedback = ({
  onBack,
  onClose,
  isFlagshipUser,
}: {
  onBack: () => void
  onClose: () => void
  isFlagshipUser: boolean
}) => {
  useTrackScreenView({ screen_name: 'negative_feedback' })
  const { trackCta } = useTrackClick()
  const { trackEvent } = useTrackEvents()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const t = useCommonTx('feedback')
  const ct = useCommonTx('common')

  const texts = {
    title: isFlagshipUser ? t('negativeFlagship.title') : t('negative.title'),
    p1: isFlagshipUser ? t('negativeFlagship.p1') : t('negative.p1'),
    inputPlaceholder: ct('commentsOrQuestions'),
    yes: ct('submitFeedback'),
    no: ct('cancel'),
  }

  const _onBack = () => {
    trackCta({
      type: 'button',
      text: '<',
      screen_name: 'negative_feedback',
      click_name: 'negative_feedback_back',
      destination: 'provide_feedback',
    })
    onBack()
  }

  const _onSubmit = () => {
    trackCta({
      type: 'button',
      text: texts.yes,
      screen_name: 'negative_feedback',
      click_name: 'negative_feedback_submit',
      destination: 'home',
    })
    trackEvent('feedback_submit', {
      feedback: textAreaRef.current?.value || '',
      screen_name: 'negative_feedback',
    })
    onClose()
  }

  const _onCancel = () => {
    trackCta({
      type: 'button',
      text: texts.no,
      screen_name: 'negative_feedback',
      click_name: 'negative_feedback_cancel',
      destination: 'home',
    })

    onClose()
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      className='relative w-full max-w-[592px] flex flex-col items-center p-6 sm:p-[64px] rounded-lg'
    >
      <Button kind='icon' size='none' className='absolute top-4 left-4' onClick={_onBack}>
        <ChevronLeftIcon title='Close' className='w-8 h-8' />
      </Button>

      <div className='w-full flex flex-col items-center justify-center text-center mx-auto'>
        <h2 className='text-heading-3 mb-4'>{texts.title}</h2>
        <p className='text-body-text-2 text-text-subdued mb-8'>{texts.p1}</p>
        <textarea
          ref={textAreaRef}
          placeholder={texts.inputPlaceholder}
          className='w-full h-[200px] border border-border-subdued rounded-md py-3 px-4 mb-8 text-body-text-2'
        />
        <div className='w-full flex flex-col items-center justify-center text-center max-w-[300px] gap-3'>
          <Button kind='primary' className='w-full' onClick={_onSubmit}>
            {texts.yes}
          </Button>
          <Button kind='secondary' className='w-full' onClick={_onCancel}>
            {texts.no}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
